import React, { useState, useMemo } from "react";
import { PropTypes } from "prop-types";
import {
    Typography,
    TextField,
    Button,
    Stack,
    Alert,
    Box,
} from "@mui/material";
import { translate } from "@flow-frontend-npm/common";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { logon } from "api";


import useMobile from "../../../flow-core-components-shared/src/hooks/useMobileView";
import translations from "../translations.json";
import {
    ERROR_TYPE_INVALID_CREDENTIALS,
    ERROR_TYPE_LOGIN_FAILED,
    formDefaultValues,
    formValidationSchema,
} from "../consts";

// eslint-disable-next-line require-jsdoc
const t = (key) => translate(translations, key);

/** Form content component
 * @param {function} setLoading
 * @return {JSX.Element}
 */
const FormContent = ({ setLoading }) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: formDefaultValues,
        resolver: yupResolver(formValidationSchema),
        reValidateMode: "onSubmit",
    });

    const [alertMsg, setAlertMsg] = useState(null);
    const isMobile = useMobile();

    const formHasError = useMemo(
        () => Object.keys(errors).length > 0,
        [errors],
    );

    /** Handling form submit event
     * @param {object} formValues
     */
    const onFormSubmit = (formValues) => {
        setLoading(true);

        logon(formValues)
            .then(({data}) => {
                if(data.redirectionUrl) {
                    window.location.href = data.redirectionUrl;
                    return;
                }
                
                window.location.reload();
            })
            .catch((error) => {
                const status = error?.response?.status;
                const errorType = error?.response?.data?.type;

                // FLOW5-4364: HTTP 429 means the login endpoint throttled us after
                // too many failed attempts. Every other credential failure now
                // arrives as the single generic LOGIN_FAILED type and shows one
                // message that does not reveal whether the username exists.
                let key;
                if (status === 429) {
                    key = "alertMessages.tooManyAttempts";
                } else if (
                    errorType === ERROR_TYPE_LOGIN_FAILED ||
                    errorType === ERROR_TYPE_INVALID_CREDENTIALS
                ) {
                    key = "alertMessages.invalidCredentials";
                } else {
                    key = "alertMessages.unhandledFailure";
                }

                setAlertMsg(t(key));
            })
            .finally(() => setLoading(false));
    };

    return (
        <Box
            sx={{
                p: 3,
                backgroundColor: ({ palette }) => palette.grey[50],
                borderRadius: "12px",
            }}
        >
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <Stack maxWidth="420px" spacing={3} mx="auto">
                    <Typography fontSize="20px" fontWeight={500}>
                        {t("title")}
                    </Typography>

                    {alertMsg || formHasError ? (
                        <Alert severity="error">
                            {formHasError
                                ? t("alertMessages.invalidCredentials")
                                : alertMsg}
                        </Alert>
                    ) : null}

                    <Controller
                        name="username"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                id="in_loginName"
                                autoFocus
                                fullWidth
                                label={t("username")}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        )}
                    />

                    <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                id="in_loginPassword"
                                type="password"
                                fullWidth
                                label={t("password")}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        )}
                    />

                    <Button
                        type="submit"
                        id="btn_loginSubmit"
                        sx={{
                            alignSelf: "end",
                            mr: ({ spacing }) =>
                                isMobile
                                    ? undefined
                                    : `${spacing(3)} !important`,
                        }}
                        color="primary"
                        variant="contained"
                    >
                        {t("login")}
                    </Button>
                </Stack>
            </form>
        </Box>
    );
};

FormContent.propTypes = {
    setLoading: PropTypes.func,
};

export default FormContent;
